import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setGoogleCropData,
  setGoogleCropDataForAspectRatio,
  setGoogleImageDataForRatio,
} from '@/editor/store/reducers/creative-step.slice';
import { blobToDataURL, calculateCenteredCrop } from '@/editor/components/media-library/images/services/imageUtilities';
import { GooglePreviewRef } from './GooglePreview';
import { IMediaLibraryImageModel } from '@/shared/models/media-library/media-library-image.model';
import {
  cropImageProgrammatically,
  loadAndCrop,
} from '@/editor/pages/create-ads-steps-page/components/creatives-step/services';

export const useGoogleCropperLogic = (
  googlePreviewRef: React.RefObject<GooglePreviewRef | null>,
  creativeMedia: IMediaLibraryImageModel
) => {
  const [cropBoxStates, setCropBoxStates] = useState<{ [key: number]: Cropper.CropBoxData }>({});
  const [currentRatio, setCurrentRatio] = useState(1.91);
  const dispatch = useDispatch();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const ratioRef = useRef(currentRatio);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    ratioRef.current = currentRatio;
  }, [currentRatio]);

  useEffect(() => {
    const initializeDefaultCrops = async () => {
      if (!creativeMedia || Object.keys(cropBoxStates).length > 0) {
        return;
      }

      const imageDimensions = {
        width: creativeMedia.width,
        height: creativeMedia.height,
      };

      const defaultSquareCrop = calculateCenteredCrop(imageDimensions, 1);
      const defaultLandscapeCrop = calculateCenteredCrop(imageDimensions, 1.91);

      const initialCropState = {
        1: defaultSquareCrop,
        1.91: defaultLandscapeCrop,
      };

      dispatch(setGoogleCropData(initialCropState));
      setCropBoxStates(initialCropState);
      try {
        console.log('Initializing default blobs...');

        const squareCropForUtil = {
          x: defaultSquareCrop.left,
          y: defaultSquareCrop.top,
          width: defaultSquareCrop.width,
          height: defaultSquareCrop.height,
        };
        const landscapeCropForUtil = {
          x: defaultLandscapeCrop.left,
          y: defaultLandscapeCrop.top,
          width: defaultLandscapeCrop.width,
          height: defaultLandscapeCrop.height,
        };

        const [squareBlob, landscapeBlob] = await Promise.all([
          loadAndCrop(creativeMedia.id, squareCropForUtil),
          loadAndCrop(creativeMedia.id, landscapeCropForUtil),
        ]);

        const [squareDataUrl, landscapeDataUrl] = await Promise.all([
          blobToDataURL(squareBlob),
          blobToDataURL(landscapeBlob),
        ]);

        if (squareDataUrl) {
          dispatch(setGoogleImageDataForRatio({ ratio: 1, imageData: squareDataUrl }));
        }

        if (landscapeDataUrl) {
          dispatch(
            setGoogleImageDataForRatio({
              ratio: 1.91,
              imageData: landscapeDataUrl,
            })
          );
        }

        console.log('Default blobs initialized and stored in Redux.');
      } catch (error) {
        console.error('Failed to initialize default cropped blobs:', error);
      }
    };

    initializeDefaultCrops();

    // if (creativeMedia && Object.keys(cropBoxStates).length === 0) {
    //   const imageDimensions = {
    //     width: creativeMedia.width,
    //     height: creativeMedia.height,
    //   };

    //   const defaultSquareCrop = calculateCenteredCrop(imageDimensions, 1);
    //   const defaultLandscapeCrop = calculateCenteredCrop(imageDimensions, 1.91);

    //   const initialCropState: { [key: number]: Cropper.CropBoxData } = {
    //     1: defaultSquareCrop,
    //     1.91: defaultLandscapeCrop,
    //   };

    //   setCropBoxStates(initialCropState);

    //   const preview = googlePreviewRef.current;
    //   if (!preview) return;

    //   preview.setCropBoxData(initialCropState[ratioRef.current]);

    //   dispatch(setGoogleCropData(initialCropState));
    // }
  }, []);

  const handleAspectRatioClick = (newRatio: number) => {
    const preview = googlePreviewRef.current;
    if (!preview) return;

    if (newRatio === currentRatio) {
      const currentCropBox = preview.getCropBoxData();
      if (currentCropBox) {
        setCropBoxStates((prev) => ({
          ...prev,
          [currentRatio]: currentCropBox,
        }));
      }
      return;
    }

    const oldCropBoxData = preview.getCropBoxData();
    const cropBoxToRestore = cropBoxStates[newRatio];

    preview.setAspectRatio(newRatio);
    if (cropBoxToRestore) {
      preview.setCropBoxData(cropBoxToRestore);
    }

    if (oldCropBoxData) {
      setCropBoxStates((prev) => ({
        ...prev,
        [currentRatio]: oldCropBoxData,
      }));
    }
    setCurrentRatio(newRatio);
  };

  const handleCropChange = useCallback(
    async (cropData: Cropper.CropBoxData) => {
      dispatch(setGoogleCropDataForAspectRatio({ ratio: ratioRef.current, cropBox: cropData }));

      if (debounceTimerRef.current) {
        console.log('clearing debounce ');
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        console.log('Debounce timer finished. Generating blob...');

        const preview = googlePreviewRef.current;
        if (!preview) return;

        const blob = await preview.getCroppedBlob();
        if (!blob) return;

        const blobUrl = await blobToDataURL(blob);
        if (!blobUrl) return;

        dispatch(setGoogleImageDataForRatio({ ratio: ratioRef.current, imageData: blobUrl }));
      }, 500);
    },
    [dispatch]
  );

  return {
    currentRatio,
    handleAspectRatioClick,
    handleCropChange,
  };
};
